import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import client from '../api/client';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

export default function Register() {
  const { t } = useLanguage();
  // Step management
  const [step, setStep] = useState<1 | 2>(1);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referrerEmail, setReferrerEmail] = useState('');
  
  // UI state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Timer state
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  
  const navigate = useNavigate();

  // Countdown timer effect for code expiration (10 minutes)
  useEffect(() => {
    if (!codeExpiresAt) return;
    
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((codeExpiresAt - Date.now()) / 1000));
      setTimeRemaining(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
        setError(t.codeExpired);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [codeExpiresAt]);

  // Resend cooldown timer (60 seconds)
  useEffect(() => {
    if (resendCooldown <= 0) return;
    
    const interval = setInterval(() => {
      setResendCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Step 1: Send verification code
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email format
    if (!isValidEmail(email)) {
      setError(t.invalidEmail);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await client.post('/auth/send-code', { email });
      
      // Success: transition to step 2
      setStep(2);
      setCodeExpiresAt(Date.now() + 10 * 60 * 1000); // 10 minutes
      setTimeRemaining(10 * 60);
      setResendCooldown(60); // 60 second cooldown
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || t.sendCodeFailed);
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      await client.post('/auth/send-code', { email });
      
      // Reset timers
      setCodeExpiresAt(Date.now() + 10 * 60 * 1000);
      setTimeRemaining(10 * 60);
      setResendCooldown(60);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || t.sendCodeFailed);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Register with verification code
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate password match
    if (password !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }
    
    // Validate password length
    if (password.length < 6) {
      setError(t.passwordTooShort);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const res = await client.post('/auth/register', {
        email,
        code,
        password,
        referrer_email: referrerEmail.trim() || undefined,
      });
      
      // Success: store token and navigate to dashboard
      localStorage.setItem('token', res.data.access_token);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || t.registerFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSwitcher />
      </div>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.brand}>{t.brand}</h1>
          <p style={styles.slogan}>{t.slogan}</p>
          <p style={styles.tagline}>{t.tagline}</p>
          <p style={styles.domain}>{t.domain}</p>
        </div>
        
        <h2 style={styles.title}>{t.registerTitle}</h2>
        
        {/* 为什么选择我们 */}
        <div style={{ background: '#f0f7ff', border: '1px solid #91caff', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0958d9', marginBottom: '10px', marginTop: 0 }}>为什么不选便宜机场，选我们？</h4>
          <div style={{ fontSize: '12px', lineHeight: '1.6', color: '#333' }}>
            <p style={{ margin: '0 0 8px 0' }}><strong>便宜机场的套路：</strong>年付/终身套餐 → 用几个月 → 跑路了。<strong>我们只卖月付，不打算跑路。</strong></p>
            <p style={{ margin: '0 0 8px 0' }}><strong>AWS 海外服务器</strong>，不存在"被国内机房一锅端"的风险。</p>
            <p style={{ margin: '0 0 8px 0' }}><strong>每个 IP 只服务 30 人</strong>，流量特征分散，更难被封锁。</p>
            <p style={{ margin: '0 0 8px 0' }}><strong>Hysteria2 + 跳频</strong>，抗封锁能力最强，自动切换持续可用。</p>
            <p style={{ margin: '0 0 8px 0' }}><strong>前 Google/Amazon/微软工程师</strong>，肉身在美国，响应快。</p>
            <p style={{ margin: '0', padding: '10px', background: '#fff', borderRadius: '4px', border: '1px solid #91caff' }}><strong>免费试用 3天/1GB</strong>，月付 ¥29，随时可以不续。</p>
          </div>
        </div>
        
        <div style={styles.trialNotice}>
          {t.registerPromo}
        </div>
        
        {error && <p style={styles.error}>{error}</p>}
        
        {step === 1 ? (
          <form onSubmit={handleSendCode}>
            <input
              style={styles.input}
              type="email"
              placeholder={t.email}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <input
              style={{ ...styles.input, marginBottom: '4px' }}
              type="email"
              placeholder={t.referralEmailPlaceholder}
              value={referrerEmail}
              onChange={e => setReferrerEmail(e.target.value)}
            />
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '16px', paddingLeft: '4px' }}>
              {t.referralEmailLabel}
            </div>
            <button style={styles.button} type="submit" disabled={loading}>
              {loading ? t.sendingCode : t.sendCode}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div style={styles.infoText}>
              {t.codeSentTo} <strong>{email}</strong>
            </div>

            {referrerEmail && (
              <div style={{ ...styles.infoText, color: '#52c41a', background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                {t.referralEmailLabel}：<strong>{referrerEmail}</strong><br />
                <span style={{ fontSize: '13px' }}>{t.referralConfirm}</span>
              </div>
            )}
            
            {timeRemaining > 0 && (
              <div style={styles.timerText}>
                {t.codeExpiry}{formatTime(timeRemaining)}
              </div>
            )}
            
            <input
              style={styles.input}
              type="text"
              placeholder={t.verificationCode}
              value={code}
              onChange={e => setCode(e.target.value)}
              maxLength={6}
              required
            />
            
            <input
              style={styles.input}
              type="password"
              placeholder={t.setPassword}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            
            <input
              style={styles.input}
              type="password"
              placeholder={t.confirmPassword}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
            
            <button style={styles.button} type="submit" disabled={loading || timeRemaining === 0}>
              {loading ? t.registering : t.registerButton}
            </button>
            
            <div style={styles.resendContainer}>
              {resendCooldown > 0 ? (
                <span style={styles.resendDisabled}>
                  {resendCooldown} {t.resendCooldown}
                </span>
              ) : (
                <button
                  type="button"
                  style={styles.resendButton}
                  onClick={handleResendCode}
                  disabled={loading}
                >
                  {t.resendCode}
                </button>
              )}
            </div>
            
            <button
              type="button"
              style={styles.backButton}
              onClick={() => {
                setStep(1);
                setCode('');
                setPassword('');
                setConfirmPassword('');
                setError('');
                setCodeExpiresAt(null);
              }}
            >
              {t.backToEmail}
            </button>
          </form>
        )}
        
        <p style={styles.link}>
          {t.hasAccount}<Link to="/login" style={styles.linkText}>{t.loginNow}</Link>
        </p>
        
        <div style={styles.legalLinks}>
          <a href="/user-agreement" style={styles.legalLink}>{t.userAgreement}</a>
          <span style={styles.linkSeparator}>|</span>
          <a href="/privacy-policy" style={styles.legalLink}>{t.privacyPolicy}</a>
          <span style={styles.linkSeparator}>|</span>
          <a href="/terms-of-service" style={styles.legalLink}>{t.termsOfService}</a>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    minHeight: '100vh', 
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    background: '#fff', 
    padding: '48px 40px', 
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)', 
    width: '100%',
    maxWidth: '420px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '2px solid #f0f0f0',
  },
  brand: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#667eea',
    margin: '0 0 8px 0',
  },
  slogan: {
    fontSize: '14px',
    color: '#888',
    margin: '0 0 4px 0',
  },
  tagline: {
    fontSize: '13px',
    color: '#667eea',
    margin: '0 0 8px 0',
    fontWeight: '500',
  },
  domain: {
    fontSize: '13px',
    color: '#667eea',
    margin: 0,
    fontWeight: '500',
  },
  title: { 
    textAlign: 'center', 
    marginBottom: '20px', 
    color: '#333',
    fontSize: '24px',
    fontWeight: '600',
  },
  trialNotice: {
    textAlign: 'center',
    padding: '14px',
    background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)',
    border: '2px solid #91d5ff',
    borderRadius: '8px',
    color: '#0050b3',
    fontSize: '15px',
    marginBottom: '24px',
    fontWeight: 'bold',
  },
  input: {
    width: '100%', 
    padding: '14px 16px', 
    marginBottom: '16px',
    border: '2px solid #e8e8e8', 
    borderRadius: '8px', 
    fontSize: '15px',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s',
    outline: 'none',
  },
  button: {
    width: '100%', 
    padding: '14px', 
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', 
    border: 'none', 
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
  },
  error: { 
    color: '#ff4d4f', 
    textAlign: 'center', 
    marginBottom: '16px',
    padding: '12px',
    background: '#fff2f0',
    borderRadius: '8px',
    fontSize: '14px',
  },
  link: { 
    textAlign: 'center', 
    marginTop: '24px', 
    fontSize: '14px',
    color: '#666',
  },
  linkText: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '600',
    marginLeft: '4px',
  },
  infoText: {
    textAlign: 'center',
    marginBottom: '16px',
    fontSize: '14px',
    color: '#666',
    padding: '12px',
    background: '#f9f9f9',
    borderRadius: '8px',
  },
  timerText: {
    textAlign: 'center',
    marginBottom: '16px',
    fontSize: '14px',
    color: '#667eea',
    fontWeight: 'bold',
  },
  resendContainer: {
    textAlign: 'center',
    marginTop: '16px',
  },
  resendButton: {
    background: 'none',
    border: 'none',
    color: '#667eea',
    cursor: 'pointer',
    fontSize: '14px',
    textDecoration: 'underline',
    fontWeight: '600',
  },
  resendDisabled: {
    color: '#999',
    fontSize: '14px',
  },
  backButton: {
    width: '100%',
    padding: '12px',
    marginTop: '12px',
    background: '#fff',
    color: '#666',
    border: '2px solid #e8e8e8',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  legalLinks: {
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  legalLink: {
    color: '#999',
    textDecoration: 'none',
    fontSize: '12px',
    transition: 'color 0.2s'
  },
  linkSeparator: {
    color: '#ddd',
    fontSize: '12px'
  }
};